import React, { useState, useEffect } from "react";
import useOffersData from "../hooks/useOffersData";
import useStockData from "../hooks/useStockData";
// import OfferProductModal from "./OfferProductModal";
import Button from "@mui/material/Button";
// import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "../context/ThemeContext";
import BulkOfferItemsTable from "./BulkOfferItemsTable";
import { getUserData } from "../utils/Auth";

const NewOfferModal = ({ isOpen, onClose, onOfferAdded }) => {
  const { addOffer } = useOffersData();
  const { allProducts, fetchAllProducts } = useStockData();
  const { darkMode } = useTheme();

  // --- State ----------------------------------------------------------------
  const [customerName, setCustomerName] = useState("");
  const [discount, setDiscount] = useState(0);
  // indulhat üres tömbbel; resetForm openkor úgyis beállítja
  const [items, setItems] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const [useMarmuristPricing, setUseMarmuristPricing] = useState(false);
  const [canAccessMarmurist, setCanAccessMarmurist] = useState(false);

  const [notita, setNotita] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [tipClient, setTipClient] = useState(0); // 0 = Persoana Fizica
  const [pretLivrare, setPretLivrare] = useState(0);
  const [pretMontaj, setPretMontaj] = useState(0);
  const [livrareRuta, setLivrareRuta] = useState("");
  const [cantitateMontaj, setCantitateMontaj] = useState(0);

  const [preferentialPrice, setPreferentialPrice] = useState("");
  const [totalPrice, setTotalPrice] = useState(0); // nettó összeg (discount+extra után)
  const [lastModifiedField, setLastModifiedField] = useState(null); // "discount" | "preferentialPrice"
  const [tva, setTva] = useState(0);
  const [totalWithTVA, setTotalWithTVA] = useState(0);
  const [totalBrutNoDiscount, setTotalBrutNoDiscount] = useState(0);
  const [itemsSubtotal, setItemsSubtotal] = useState(0); // csak termékek nettó, discount előtt

  // --- Modal openkor: töltés + reset ----------------------------------------
  useEffect(() => {
    if (isOpen) {
      fetchAllProducts();
      resetForm();
    }
  }, [isOpen]);

  // --- Ár kalkuláció (termékek + montaj + livrare + discount) ----------------
  useEffect(() => {
    const subtotal = items.reduce((acc, item) => {
      const mp = parseFloat(item.quantityMP) || 0;
      const ml = parseFloat(item.quantityML) || 0;
      const buc = parseFloat(item.quantityBUC) || 0;

      const product = allProducts.find(
        (p) => p.id === parseInt(item.productId, 10)
      );
      if (!product) return acc;

      const price = useMarmuristPricing
        ? product.priceMarmurist
        : product.price;

      let itemTotal = 0;
      if (ml > 0) itemTotal = price * ml;
      else if (mp > 0) itemTotal = price * mp;
      else if (buc > 0) itemTotal = price * buc;

      return acc + itemTotal;
    }, 0);

    const montajTotal =
      (parseFloat(pretMontaj) || 0) * (parseFloat(cantitateMontaj) || 0);
    const livrareTotal = parseFloat(pretLivrare) || 0;

    const totalNet =
      subtotal * (1 - discount / 100) + montajTotal + livrareTotal;

    const calculatedTVA = parseFloat((totalNet * 0.21).toFixed(2));
    const totalBrut = parseFloat((totalNet + calculatedTVA).toFixed(2));

    const brutNoDiscountNet = subtotal + montajTotal + livrareTotal;
    const brutNoDiscountTVA = parseFloat((brutNoDiscountNet * 0.21).toFixed(2));
    const brutNoDiscountTotal = parseFloat(
      (brutNoDiscountNet + brutNoDiscountTVA).toFixed(2)
    );

    setTotalBrutNoDiscount(brutNoDiscountTotal);
    setItemsSubtotal(subtotal);
    setTotalPrice(parseFloat(totalNet.toFixed(2)));
    setTva(calculatedTVA);
    setTotalWithTVA(totalBrut);
  }, [
    items,
    allProducts,
    useMarmuristPricing,
    pretMontaj,
    cantitateMontaj,
    pretLivrare,
    discount,
  ]);

  // Discount → PreferentialPrice
  useEffect(() => {
    if (lastModifiedField === "discount") {
      const discountedNet = itemsSubtotal * (1 - discount / 100);
      const montajTotal =
        (parseFloat(pretMontaj) || 0) * (parseFloat(cantitateMontaj) || 0);
      const livrareTotal = parseFloat(pretLivrare) || 0;
      const fullNet = discountedNet + montajTotal + livrareTotal;
      const gross = parseFloat((fullNet * 1.21).toFixed(2));
      setPreferentialPrice(gross);
    }
  }, [
    discount,
    itemsSubtotal,
    pretMontaj,
    cantitateMontaj,
    pretLivrare,
    lastModifiedField,
  ]);

  // PreferentialPrice → Discount visszaszámítás
  useEffect(() => {
    if (
      lastModifiedField === "preferentialPrice" &&
      preferentialPrice &&
      itemsSubtotal > 0
    ) {
      const montajTotal =
        (parseFloat(pretMontaj) || 0) * (parseFloat(cantitateMontaj) || 0);
      const livrareTotal = parseFloat(pretLivrare) || 0;
      const fullNet = preferentialPrice / 1.21;

      const discountOnlyForItems =
        ((itemsSubtotal - (fullNet - montajTotal - livrareTotal)) * 100) /
        itemsSubtotal;

      setDiscount(parseFloat(discountOnlyForItems.toFixed(2)));
    }
  }, [
    preferentialPrice,
    itemsSubtotal,
    pretMontaj,
    cantitateMontaj,
    pretLivrare,
    lastModifiedField,
  ]);

  // Első kalkulációkor preferențial auto kitöltés
  useEffect(() => {
    if (totalPrice > 0 && lastModifiedField === null) {
      setPreferentialPrice(parseFloat((totalPrice * 1.21).toFixed(2)));
    }
  }, [totalPrice, lastModifiedField]);

  // Marmurist hozzáférés
  useEffect(() => {
    const fetchUserData = async () => {
      const userData = await getUserData();
      if (userData?.canAccessMarmurist) {
        setCanAccessMarmurist(true);
      } else {
        setCanAccessMarmurist(false);
        setUseMarmuristPricing(false);
      }
    };

    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);

  const handleMarmuristCheckboxChange = (checked) => {
    setUseMarmuristPricing(checked);
  };

  // --- Reset ----------------------------------------------------------------
  const resetForm = () => {
    setCustomerName("");
    setDiscount(0);
    setNotita("");
    setCustomerPhone("");
    setTipClient(0);
    setPretMontaj(0);
    setCantitateMontaj(0);
    setPretLivrare(0);
    setLivrareRuta("");
    setPreferentialPrice("");
    setTotalPrice(0);
    setTva(0);
    setTotalWithTVA(0);
    setItemsSubtotal(0);
    setSuccessMessage("");
    setError(null);
    setLastModifiedField(null);
    setUseMarmuristPricing(false);
    setItems([
      {
        productId: "",
        materialName: "",
        customDescription: "",
        length: "",
        width: "",
        quantity: "",
        quantityMP: "",
        quantityML: "",
        quantityBUC: "",
        isManualMP: false,
      },
    ]);
  };

  // (nem aktív, csak marad)
  const handleAddItem = (product) => {
    if (!product || !product.productId) return;
    setItems([...items, product]);
    setIsProductModalOpen(false);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // --- Submit ---------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!customerName.trim()) {
      setError("❌ Numele clientului este obligatoriu!");
      return;
    }

    // legalább egy sor kell értelmes mennyiséggel
    const validItems = items.filter(
      (item) =>
        item.productId &&
        (parseFloat(item.quantityMP) > 0 ||
          parseFloat(item.quantityML) > 0 ||
          parseFloat(item.quantityBUC) > 0)
    );

    if (validItems.length === 0) {
      setError(
        "❌ Adaugă cel puțin un produs cu cantitate > 0 (MP / ML / BUC)!"
      );
      return;
    }

    try {
      const offerData = {
        customerName,
        telefon: customerPhone,
        discount: parseFloat(discount) || 0,
        useMarmuristPricing,
        notita,
        tipClient,
        pretMontaj,
        cantitateMontaj,
        pretLivrare,
        livrareRuta,
        preferentialPrice: parseFloat(preferentialPrice) || null,
        items: validItems.map((item) => ({
          productId: parseInt(item.productId, 10),
          quantityMP: parseFloat(item.quantityMP) || 0,
          quantityML: parseFloat(item.quantityML) || 0,
          quantityBUC: parseInt(item.quantityBUC, 10) || 0,
          length: parseFloat(item.length) || 0,
          width: parseFloat(item.width) || 0,
          quantity: parseInt(item.quantity, 10) || 0,
          customDescription: item.customDescription || "",
          // Bizot nincs
        })),
      };

      await addOffer(offerData);

      setSuccessMessage("✅ Ofertă adăugată cu succes!");
      setTimeout(() => {
        setSuccessMessage("");
        resetForm();
        onOfferAdded();
        onClose();
      }, 500);
    } catch (err) {
      setError("❌ Eroare la adăugarea ofertei!");
    }
  };

  // --- Render ---------------------------------------------------------------
  return isOpen ? (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`p-6 rounded-3xl shadow-xl w-[65%] max-w-7xl max-h-[85vh] overflow-y-auto relative transition-colors ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ❌ Bezárás */}
        <button
          onClick={onClose}
          className={`absolute top-3 right-4 text-2xl ${
            darkMode
              ? "text-red-400 hover:text-red-600"
              : "text-red-500 hover:text-red-700"
          }`}
        >
          ✖
        </button>

        <h2 className="text-2xl font-semibold mb-4 text-center text-blue-400">
          Adăugare ofertă nouă
        </h2>

        {/* ✅ Siker */}
        {successMessage && (
          <p
            className={`p-2 rounded mb-3 text-center ${
              darkMode
                ? "text-green-400 bg-green-900"
                : "text-green-700 bg-green-200"
            }`}
          >
            {successMessage}
          </p>
        )}

        {/* ❌ Hiba */}
        {error && (
          <p
            className={`p-2 rounded mb-3 text-center ${
              darkMode ? "text-red-400 bg-red-900" : "text-red-700 bg-red-200"
            }`}
          >
            {error}
          </p>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="w-full flex flex-wrap gap-4 items-end">
            {/* Nume client */}
            <div className="flex-1 min-w-[180px]">
              <label className="block font-semibold mb-1">Nume client:</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            {/* Tip client */}
            <div className="flex-1 min-w-[160px]">
              <label className="block font-semibold mb-1">Tip client:</label>
              <select
                value={tipClient}
                onChange={(e) => setTipClient(parseInt(e.target.value, 10))}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              >
                <option value={0}>Persoană Fizică</option>
                <option value={1}>Persoană Juridică</option>
              </select>
            </div>

            {/* Număr de telefon */}
            <div className="flex-1 min-w-[180px]">
              <label className="block font-semibold mb-1">
                Număr de telefon:
              </label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="^\+?\d{0,15}$"
                maxLength={15}
                value={customerPhone}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\+?\d*$/.test(val)) {
                    setCustomerPhone(val);
                  }
                }}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            {/* Folosește preț Marmurist */}
            {canAccessMarmurist && (
              <div className="min-w-[200px] flex items-center h-[60px] pt-[20px]">
                <label className="flex items-center font-semibold">
                  <input
                    type="checkbox"
                    checked={useMarmuristPricing}
                    onChange={(e) =>
                      handleMarmuristCheckboxChange(e.target.checked)
                    }
                    className="mr-2 w-5 h-5"
                  />
                  Folosește preț Marmist
                </label>
              </div>
            )}
          </div>

          {/* Notiță */}
          <div className="w-full">
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Notiță (opțional):
            </label>
            <textarea
              rows={3}
              value={notita}
              onChange={(e) => setNotita(e.target.value)}
              className={`w-full p-2 border rounded-2xl resize-none ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-300 bg-white text-black"
              }`}
              placeholder="Observații, note interne etc."
            />
          </div>

          {/* Livrare / Montaj */}
          <div className="w-full flex gap-4 flex-wrap">
            <div className="w-[220px]">
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Preț montaj (EUR):
              </label>
              <input
                type="number"
                min="0"
                value={pretMontaj}
                onChange={(e) => setPretMontaj(parseFloat(e.target.value))}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            <div className="w-[220px]">
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Cantitate montaj:
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={cantitateMontaj}
                onChange={(e) => setCantitateMontaj(parseFloat(e.target.value))}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            <div className="w-[220px]">
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Preț livrare (EUR):
              </label>
              <input
                type="number"
                min="0"
                value={pretLivrare}
                onChange={(e) => setPretLivrare(parseFloat(e.target.value))}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            <div className="flex-1 min-w-[220px]">
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Rută livrare (opțional):
              </label>
              <input
                type="text"
                placeholder="Ex: Oradea - Cluj"
                value={livrareRuta}
                onChange={(e) => setLivrareRuta(e.target.value)}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>
          </div>

          {/* Terméktábla */}
          <h3
            className={`text-lg font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Produse
          </h3>

          <BulkOfferItemsTable
            products={allProducts}
            offerItems={items}
            setOfferItems={setItems}
            darkMode={darkMode}
          />

          {/* Total brut + Preferential + Discount mezők */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div>
              <label
                className={`block mb-1 font-semibold ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Total brut (TVA inclus, fără discount)
              </label>
              <input
                type="number"
                value={totalBrutNoDiscount}
                disabled
                className={`w-full p-2 rounded-xl ${
                  darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-black"
                }`}
              />
            </div>

            <div>
              <label
                className={`block mb-1 font-semibold ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Preț Preferențial
              </label>
              <input
                type="number"
                value={preferentialPrice}
                onChange={(e) => {
                  setPreferentialPrice(parseFloat(e.target.value));
                  setLastModifiedField("preferentialPrice");
                }}
                className={`w-full p-2 border rounded-xl ${
                  darkMode
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-black"
                }`}
              />
            </div>

            <div>
              <label
                className={`block mb-1 font-semibold ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Discount (%)
              </label>
              <input
                type="number"
                value={discount}
                onChange={(e) => {
                  setDiscount(parseFloat(e.target.value));
                  setLastModifiedField("discount");
                }}
                className={`w-full p-2 border rounded-xl ${
                  darkMode
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-black"
                }`}
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="contained"
            color="success"
            sx={{
              borderRadius: "30px",
              width: "100%",
              color: "white",
              marginTop: "20px",
              height: "56px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Salvează oferta
          </Button>
        </form>

        {/* {isProductModalOpen && (
          <OfferProductModal
            isOpen={isProductModalOpen}
            onClose={() => setIsProductModalOpen(false)}
            onAdd={handleAddItem}
            allProducts={allProducts}
            existingProducts={items}
          />
        )} */}
      </div>
    </div>
  ) : null;
};

export default NewOfferModal;
